import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { SecurityRetryService } from "@app/services/security-retry.service";
import { SignalrService } from "@app/services/signalr.service";
import { eAlertType, IClientContext, IDialogService, IKbRole, IRootScope, IScene, IUser } from "@models";
import { events } from "@tools";
import { StateService } from "@uirouter/core";
import { StorageService } from "./storage.service";

export interface IAuthService {
    authPromise: ng.IPromise<any>;

    // loadCompany: () => ng.IPromise<any>;
    // loadUser: () => ng.IPromise<any>;
    loadContext: () => ng.IPromise<any>;
    // loadExchangeRates: () => ng.IPromise<any>;

    login: (email: string, password: string, rememberMe: boolean) => ng.IHttpPromise<any>;
    logout: () => void;
    confirmLogin: (user: IUser) => void;
    loginError: string;

    // canUser(permissionId: string): boolean;
    hasRole(role: string): boolean;
    userHasRole(user: any, role: string): boolean;
    // isAdmin(): boolean;
    // userIsAdmin(user: IUser): boolean;
    isAdminOrCompanyAdmin(): boolean;
}

@NgService({
    token: Token.AuthService,
    dependencies: [
        Token.$Http,
        Token.SecurityRetryService,
        Token.$RootScope,
        Token.$Q,
        Token.SignalRService,
        Token.DialogService,
        Token.$State,
        Token.StorageService
    ]
})
export class AuthService {

    constructor(
        private $http: ng.IHttpService,
        private securityRetryQueue: SecurityRetryService,
        private $rootScope: IRootScope,
        private $q: ng.IQService,
        private signalrService: SignalrService,
        private dialogService: IDialogService,
        private $state: StateService,
        private storageService: StorageService
    ) {

        // we need a promise that doesn't resolve until we have a real user.  
        // Controllers can set this promise in the resolve property of the 
        // route so that the controller won't initialize until we have a user
        let userDeferred = $q.defer();
        this.userPromise = userDeferred.promise;
        let contextDeferred = $q.defer();
        this.contextPromise = contextDeferred.promise;
        // var companyDeferred = $q.defer();
        // this.companyPromise = companyDeferred.promise;
        // var exchangeRatesDeferred = $q.defer();
        // this.exchangeRatesPromise = exchangeRatesDeferred.promise;
        // var companySettingsDeferred = $q.defer();
        // this.companySettingsPromise = companySettingsDeferred.promise;

        this.authPromise = $q.all([this.userPromise, this.contextPromise]);

        this.loadContext = () => {
            return $q.when((() => {
                let serverVm: { context: IClientContext; scene?: IScene; } = (window as any).serverVm;
                $rootScope.context = serverVm.context;
                $rootScope.environment = serverVm.context.environment;
                $rootScope.clusterEnv = serverVm.context.clusterEnv;
                $rootScope.cacheBuster = serverVm.context.cacheBuster;
                $rootScope.supportedLanguages = serverVm.context.supportedLanguages;
                $rootScope.clientLanguage = serverVm.context.clientLanguage;
                $rootScope.company = serverVm.context.company;
                $rootScope.companySettings = serverVm.context.companySettings;
                $rootScope.companyCurrencies = serverVm.context.companyCurrencies;
                fx.base = $rootScope.companySettings.currency.toUpperCase();
                fx.rates = {};
                $rootScope.companyCurrencies.forEach(cc => {
                    fx.rates[cc.currency.toUpperCase()] = cc.rate;
                });
                $rootScope.kbmaxLogoPath = storageService.getAssetUrl('images/epicor_logo_white.png');
                if ($rootScope.context.channel && $rootScope.context.channel.imagePath) {
                    $rootScope.companyLogoPath = storageService.getMediaUrl($rootScope.context.channel.imagePath);
                }
                else {
                    $rootScope.companyLogoPath = $rootScope.companySettings.logoPath ? storageService.getMediaUrl($rootScope.companySettings.logoPath) : $rootScope.kbmaxLogoPath
                }

                if (serverVm.context.user) {
                    this.confirmLogin(serverVm.context.user);
                }

                contextDeferred.resolve(serverVm.context);
            })());
        };

        this.login = (email: string, password: string, rememberMe: boolean) => {
            this.loginError = null;
            if ($rootScope.user) {
                this.logout();
            }
            return this.$http.post<IUser>(
                "/api/auth/login",
                { email, password, rememberMe },
                { doNotReplay: true, tracker: $rootScope.loginTracker } as ng.IRequestConfig
            ).then(r => {
                this.confirmLogin(r.data);
            }, (r: ng.IHttpResponse<IUser>) => {
                if (r.status == 400) {
                    this.loginError = r.data[""].join("\n");
                }
            });
        };

        this.logout = () => {
            this.$http.post("/api/auth/logout", { doNotReplay: true } as ng.IRequestConfig).then(() => {
                // reload the whole page to clear all context
                window.location.reload();
            });
            // $rootScope.user = null;
            // $rootScope.$broadcast(events.loginRequested);
        };

        $rootScope.$on(events.loginRequested, e => {
            $rootScope.user = null;
        });

        $rootScope.$on(events.permissionDenied, e => {
            this.dialogService.alert({
                msg: "Sorry, you do not have permission to view this.",
                type: eAlertType.error
            });
            this.$state.go("kb.products");
        });

        this.confirmLogin = user => {
            $rootScope.user = user;
            // resolve the deferred user promise
            userDeferred.resolve(user);

            this.securityRetryQueue.retryAll();

            if ($rootScope.user.id != -1) {
                this.signalrService.start().then(() => {
                    $rootScope.$broadcast(events.loginSuccessful, $rootScope.user);
                });
            }
            else {
                $rootScope.$broadcast(events.loginSuccessful, $rootScope.user);
            }

        };

        this.keepAlive = () => {
            this.$http.get("/api/auth/keepalive").then(() => {
                // reload the whole page to clear all context
                // window.location.reload();
            });
        }

        this.loadContext();
    }

    public static ANONYMOUS_USER_ID = -1;
    private userPromise: ng.IPromise<any>;
    public contextPromise: ng.IPromise<any>;
    public authPromise: ng.IPromise<any>;

    public loadContext: () => ng.IPromise<any>;
    public login: (email: string, password: string, rememberMe: boolean) => ng.IPromise<any>;
    public logout: () => void;
    public confirmLogin: (user: IUser) => void;
    public loginError: string;
    public keepAlive: () => void;

    public hasRole(role: string): boolean {
        let isInRole = false;

        if (this.$rootScope.user) {
            isInRole = this.$rootScope.user.roles.some((r: IKbRole) => {
                return r.role.isEqual(role);
            });
        }
        return isInRole;
    }

    public userHasRole(user: IUser, role: string): boolean {
        let isInRole = user.roles.some((r: IKbRole) => {
            if (!r.role) return false;
            return r.role.isEqual(role);
        });

        return isInRole;
    }

    public isAdminOrCompanyAdmin(): boolean {
        let bret = false;
        if (this.$rootScope.user) {
            bret = this.hasRole("Company Administrator") || this.$rootScope.user.isAdmin;
        }
        return bret;
    }

}
